// Into stock: which items on a request become inventory, the catalog item each
// one is, and the key that receives it. Outside the Items table on purpose: the
// picker's list would be clipped by the table's scroll box.

import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../components/Toast/ToastContext';
import ItemPicker from '../components/ItemPicker';
import ItemName, { itemLabel } from '../components/ItemName';
import NewItemModal from '../Parts/NewItemModal';
import DrawerShell from '../Receiving/DrawerShell';
import ReceiveDrawer, { type RequisitionPrefill } from '../Receiving/ReceiveDrawer';
import type { PartsCatalogRow } from '../Receiving/types';
import useUserStore from '../stores/useUserStore';
import { PackagePips } from './PackagesCard';
import { buttonClass } from '../components/ui/Button';
import Tag from '../components/ui/Tag';
import Alert from '../components/ui/Alert';
import { Panel } from '../components/ui/Panel';
import { ListRow, ListStack } from '../components/ui/ListRow';
import Tooltip from '../components/ui/Tooltip';
import Checkbox from '../components/ui/Checkbox';
import Reveal from '../components/ui/Reveal';
import Appear from '../components/ui/Appear';
import FadeIn from '../components/ui/FadeIn';
import { FOCUS } from './buttonStyles';
import {
  errorMessage,
  formatQuantity,
  toNumber,
  useRequisitionsApi,
  type LinePart,
  type RequisitionDetail,
  type RequisitionLine,
} from './requisitionsApi';

/** The approver's choice for one line, held until Approve sends it. */
export interface StockDraft {
  on: boolean;
  part: PartsCatalogRow | null;
}

/**
 * A line's catalog item as a picker value. ItemPicker reads only the id and the
 * name fields off a selection, so the rest of a catalog row is never read here.
 */
export const partAsRow = (p: LinePart): PartsCatalogRow =>
  ({ id: p.id, sku: p.sku, name: p.name, base_name: p.name }) as unknown as PartsCatalogRow;

interface Props {
  requisition: RequisitionDetail;
  /** Set while an approver decides: changes stay here until Approve. Null saves each change. */
  draft: Record<string, StockDraft> | null;
  onDraftChange: (lineId: string, next: StockDraft) => void;
  onUpdated: (r: RequisitionDetail) => void;
  /** Re-reads the request, when the receive drawer closes without recording a box. */
  onReload: () => void;
}

const CheckGlyph = () => (
  <svg aria-hidden="true" viewBox="0 0 16 16" fill="none" className="h-4 w-4">
    <path
      fill="currentColor"
      d="M13.28 4.22a.75.75 0 0 1 0 1.06l-6.5 6.5a.75.75 0 0 1-1.06 0l-3-3a.75.75 0 1 1 1.06-1.06l2.47 2.47 5.97-5.97a.75.75 0 0 1 1.06 0Z"
    />
  </svg>
);

const StockCard: React.FC<Props> = ({ requisition: req, draft, onDraftChange, onUpdated, onReload }) => {
  const reqApi = useRequisitionsApi();
  const toast = useToast();
  const hasCapability = useUserStore((s) => s.hasCapability);
  const [busy, setBusy] = useState<string | null>(null);
  // The checkbox moves on the click, not on the answer.
  const [optimistic, setOptimistic] = useState<Record<string, boolean>>({});
  const [mintFor, setMintFor] = useState<{ lineId: string; name: string } | null>(null);
  const [receiving, setReceiving] = useState<{ title: string; prefill: RequisitionPrefill } | null>(null);
  const [receiveDirty, setReceiveDirty] = useState(false);
  const [confirmOff, setConfirmOff] = useState<string | null>(null);
  // Set once the drawer has recorded the box: the request it hands back is
  // current, and a reload could 404 for receiving once nothing is left to stock.
  const recorded = useRef(false);

  const c = req.can;
  const drafting = draft !== null;
  const lines = [...req.lines].sort((a, b) => a.line_number - b.line_number);
  const shown = c.tick_inventory ? lines : lines.filter((l) => l.add_to_inventory);
  if (!shown.length) return null;

  const tracked = lines.filter((l) => l.add_to_inventory);
  const inStock = tracked.filter((l) => l.receipt_item_id).length;
  const receivable = req.status === 'bought' || req.status === 'arrived';
  // The item page sits behind the receiving gate; anyone else reads the words.
  const canOpenBox = hasCapability('operations', 'manufacturing', 'procurement');

  const isOn = (l: RequisitionLine) =>
    draft ? !!draft[l.id]?.on : l.id in optimistic ? optimistic[l.id] : l.add_to_inventory;
  const partOf = (l: RequisitionLine): PartsCatalogRow | null =>
    draft ? draft[l.id]?.part ?? null : l.part ? partAsRow(l.part) : null;

  const save = async (l: RequisitionLine, on: boolean, partId?: number) => {
    setBusy(l.id);
    setOptimistic((o) => ({ ...o, [l.id]: on }));
    try {
      const updated = await reqApi.setLineInventory(req.id, l.id, {
        add_to_inventory: on,
        ...(partId !== undefined ? { part_catalog_id: partId } : {}),
      });
      onUpdated(updated);
    } catch (err) {
      toast.error(errorMessage(err, 'That did not save. Check your connection and try again.'));
    } finally {
      setOptimistic((o) => {
        const { [l.id]: _done, ...rest } = o;
        return rest;
      });
      setBusy(null);
    }
  };

  const toggle = (l: RequisitionLine, on: boolean) => {
    if (drafting) onDraftChange(l.id, { on, part: on ? partOf(l) : null });
    else save(l, on);
  };

  const pick = (l: RequisitionLine, row: PartsCatalogRow) => {
    if (drafting) onDraftChange(l.id, { on: true, part: row });
    else save(l, true, row.id);
  };

  const record = async (l: RequisitionLine, item: { id: string; barcode: string }) => {
    setBusy(`record-${l.id}`);
    try {
      onUpdated(await reqApi.markLineReceived(req.id, l.id, item.id));
      toast.success(`Recorded box ${item.barcode} on ${req.req_number}.`);
    } catch (err) {
      toast.error(errorMessage(err, 'That did not save. Check your connection and try again.'));
      onReload();
    } finally {
      setBusy(null);
    }
  };

  const closeReceive = () => {
    setReceiving(null);
    if (!recorded.current) onReload();
  };

  const openReceive = async (l: RequisitionLine) => {
    if (l.part_catalog_id === null) return;
    setBusy(`receive-${l.id}`);
    try {
      const hint = await reqApi.costHint(req.id, l.id);
      const part = l.part ? { sku: l.part.sku, name: l.part.name } : null;
      recorded.current = false;
      setReceiving({
        title: `Receive into stock: ${part ? itemLabel({ base_name: part.name, sku: part.sku }) : l.description}`,
        prefill: {
          requisition_id: req.id,
          line_id: l.id,
          req_number: req.req_number,
          part_catalog_id: l.part_catalog_id,
          quantity: toNumber(l.quantity),
          unit_cost_cents: toNumber(hint?.unit_cost_cents),
          cost_source: hint?.cost_source ?? null,
          part,
        },
      });
    } catch (err) {
      toast.error(errorMessage(err, 'Could not open the receive form. Check your connection and try again.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Panel
      title="Into stock"
      padding="none"
      className="mt-3"
      actions={
        receivable && tracked.length > 0 ? (
          <span className="inline-flex items-center gap-2 text-sm font-normal tabular-nums text-on-surface-variant">
            <PackagePips arrived={inStock} total={tracked.length} />
            {inStock} of {tracked.length} in stock
          </span>
        ) : undefined
      }
    >
      <Reveal show={!!c.tick_inventory}>
        <p className="m-0 px-4 pb-3 text-sm text-on-surface-variant">
          {drafting
            ? 'Tick what goes into inventory once it arrives. It is saved when you approve.'
            : 'Tick what goes into inventory once it arrives. Each change saves as you make it.'}
        </p>
      </Reveal>

      <ListStack
        items={shown}
        getKey={(l) => l.id}
        on="panel"
        label="Lines going into stock"
        className="px-2 pb-2"
        renderRow={(l) => {
          const on = isOn(l);
          const part = partOf(l);
          const received = !!l.receipt_item_id;
          const editable = c.tick_inventory && !received;
          // Once bought nothing new goes on, but a tracked line may still come off.
          const removable = c.set_inventory && !c.tick_inventory && l.add_to_inventory && !received;
          // A buyer may still name the item on a bought request, where the tick is
          // settled: there the picker shows only for a line still missing one.
          const picking = on && !received && c.set_catalog && (editable || !part);
          const needsItem = on && !part && !received;
          const unrecorded = received ? null : l.unrecorded_box;
          const ready = receivable && c.receive_lines && l.add_to_inventory && l.part_catalog_id !== null && !received;
          const canReceive = ready && !unrecorded;

          return (
            <ListRow>
              <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <div className="min-w-[10rem] flex-1">
                  <div className="break-words text-sm text-on-surface">{l.description}</div>
                  <div className="text-xs tabular-nums text-on-surface-variant">
                    Qty {formatQuantity(l.quantity)}
                  </div>
                </div>

                <Appear show={!!needsItem}>
                  <Tag tone="warn">Needs a catalog item</Tag>
                </Appear>

                {editable && (
                  <Checkbox
                    className="whitespace-nowrap py-1"
                    label="Add to inventory"
                    checked={on}
                    disabled={busy !== null}
                    onChange={(e) => toggle(l, e.target.checked)}
                    aria-label={`Add to inventory: ${l.description}`}
                  />
                )}

                {removable &&
                  (confirmOff === l.id ? (
                    <span className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() => {
                          setConfirmOff(null);
                          save(l, false);
                        }}
                        className={buttonClass('quiet', 'sm')}
                      >
                        Take it off
                      </button>
                      <button type="button" onClick={() => setConfirmOff(null)} className={buttonClass('quiet', 'sm')}>
                        Keep it
                      </button>
                    </span>
                  ) : (
                    <Tooltip label="It stays on the request as a supply. Once bought it cannot go back into inventory.">
                      <button
                        type="button"
                        disabled={busy !== null}
                        onClick={() => setConfirmOff(l.id)}
                        className={buttonClass('quiet', 'sm')}
                      >
                        {busy === l.id ? 'Saving…' : 'Not for stock after all'}
                      </button>
                    </Tooltip>
                  ))}

                {canReceive && (
                  <Tooltip label="Opens the receive form with this item and its cost filled in">
                    <button
                      type="button"
                      disabled={busy !== null}
                      onClick={() => openReceive(l)}
                      className={buttonClass('quiet', 'sm')}
                    >
                      {busy === `receive-${l.id}` ? 'Opening…' : 'Receive into stock'}
                    </button>
                  </Tooltip>
                )}

                {received &&
                  (canOpenBox ? (
                    <Link
                      to={`/receiving/item/${l.receipt_item_id}`}
                      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded text-sm text-emerald-300 hover:underline ${FOCUS}`}
                    >
                      <CheckGlyph />
                      In stock
                    </Link>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-emerald-300">
                      <CheckGlyph />
                      In stock
                    </span>
                  ))}
              </div>

              <Reveal show={!!unrecorded}>
                {unrecorded && (
                <Alert
                  tone="warning"
                  variant="note"
                  live={false}
                  className="!flex w-full"
                  action={
                    ready ? (
                      <Tooltip label="Records the box already received. Nothing is received again.">
                        <button
                          type="button"
                          disabled={busy !== null}
                          onClick={() => record(l, unrecorded)}
                          className={buttonClass('quiet', 'sm')}
                        >
                          {busy === `record-${l.id}` ? 'Recording…' : 'Record it'}
                        </button>
                      </Tooltip>
                    ) : undefined
                  }
                >
                  Box <span className="font-mono">{unrecorded.barcode}</span> is in stock but not recorded on this
                  request.
                </Alert>
                )}
              </Reveal>

              <FadeIn key={picking ? 'pick' : 'chosen'}>
              {picking ? (
                <div className="max-w-md">
                  <ItemPicker
                    id={`stock-item-${l.id}`}
                    label="Catalog item"
                    value={part}
                    // Typing over a chosen item reports null; the choice stands until
                    // another is picked or the line is unticked.
                    onSelect={(row) => {
                      if (row) pick(l, row);
                    }}
                    onCreate={(name) => setMintFor({ lineId: l.id, name })}
                    disabled={busy !== null}
                    placeholder="Search by name, SKU or part number"
                  />
                </div>
              ) : (
                on &&
                part && (
                  <div className="text-sm">
                    <ItemName size="sm" item={part} />
                  </div>
                )
              )}
              </FadeIn>
              </div>
            </ListRow>
          );
        }}
      />

      {mintFor && (
        <NewItemModal
          initialName={mintFor.name}
          onClose={() => setMintFor(null)}
          onMinted={(minted) => {
            const line = req.lines.find((x) => x.id === mintFor.lineId);
            setMintFor(null);
            if (line) pick(line, minted);
          }}
        />
      )}

      {receiving && (
        <DrawerShell title={receiving.title} onClose={closeReceive} dirty={receiveDirty}>
          <ReceiveDrawer
            requisitionPrefill={receiving.prefill}
            onClose={closeReceive}
            onReceived={closeReceive}
            onDirtyChange={setReceiveDirty}
            onRecorded={(updated) => {
              recorded.current = true;
              onUpdated(updated);
            }}
          />
        </DrawerShell>
      )}
    </Panel>
  );
};

export default StockCard;
